import * as React from "react";

import * as Cmp from "@impero-com/common/utils/comparison";
import { formatUserName } from "@impero-com/common/utils/formatting";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { Tag } from "@impero-com/ui";
import { Popover } from "antd";

import { StandardAccessRight, UserOverview } from "api/generated/model";
import {
    ACCESS_RIGHTS,
    ACCESS_RIGHT_ORDERS,
} from "utils/accessRights/constants";

import * as theme from "./theme.css";
import { AccessRightTagsData } from "./types";

type AccessRightTagsProps<U extends UserOverview> = AccessRightTagsData<U>;

class AccessRightTags<U extends UserOverview> extends React.Component<
    AccessRightTagsProps<U>
> {
    popupContent = (users: U[]) => {
        return (
            <div className={theme.tagPopUpContainer}>
                {users.map((user) => (
                    <Tag wrap key={user.id} className={theme.tagListTag}>
                        {formatUserName(user)}
                    </Tag>
                ))}
            </div>
        );
    };

    render() {
        const { usersByAccessRight } = this.props;
        const sortedAccessRights = Array.from(usersByAccessRight.keys()).sort(
            Cmp.map(
                (accessRight: StandardAccessRight) =>
                    ACCESS_RIGHT_ORDERS[accessRight],
                Cmp.primitive
            )
        );

        return (
            <div className={theme.tagsContainer}>
                {sortedAccessRights.map((accessRight) => {
                    const users = usersByAccessRight.get(accessRight);
                    if (!users || users.length === 0) {
                        return null;
                    } else {
                        return (
                            <Popover
                                key={accessRight}
                                content={this.popupContent(users)}
                            >
                                <Tag
                                    wrap
                                    variant="accessRights"
                                    className={theme.tagAccessRight}
                                >
                                    <b>
                                        {getLocalizedAccessRight(accessRight)}
                                    </b>{" "}
                                    ({users.length})
                                </Tag>
                            </Popover>
                        );
                    }
                })}
            </div>
        );
    }
}

function getLocalizedAccessRight(accessRight: StandardAccessRight): string {
    let localizedAccessRightLabel: string;
    switch (accessRight) {
        case ACCESS_RIGHTS.ADMIN:
            localizedAccessRightLabel = t("AccessRights_TagLabel_Admin");
            break;
        case ACCESS_RIGHTS.EDIT:
            localizedAccessRightLabel = t("AccessRights_TagLabel_Edit");
            break;
        case ACCESS_RIGHTS.VIEW:
            localizedAccessRightLabel = t("AccessRights_TagLabel_View");
            break;
        default:
            return assertNever(accessRight);
    }

    return localizedAccessRightLabel;
}

export { AccessRightTags, type AccessRightTagsProps };
